import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DisputeForm } from "src/app/models/dispute-form.model";
import { DisputeFormService } from "src/app/services/dispute-form.service";
import { NavigationInfo } from "src/app/services/models/navigationInfo.model";
import { ProgressInfo } from "src/app/services/models/progressInfo.model";
import { NavigationService } from "src/app/services/navigation.service";
import { ProgressBarService } from "src/app/services/progress-bar.service";
import { DFUtils } from "src/app/utils/dispute-form.utils";
import { NavigationEnum } from "../../globalvar";
import { Common } from "src/app/utils/common";
import Swal from "sweetalert2";
import { TranslateService } from "@ngx-translate/core";

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-right",
  iconColor: "white",
  customClass: {
    popup: "colored-toast",
  },
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
});

/**
 * Represents the current step in the navigation process.
 * @type {NavigationEnum}
 */
const currentStep = NavigationEnum.MC_INCIDENT_OVERVIEW;
const stepCondition = "MONEYCODE";

/**
 * MoneyCodeIncidentOverviewComponent class represents the component responsible for displaying the overview of a money code incident.
 * It handles the form validation and navigation for the money code incident overview page.
 */
@Component({
  selector: "app-money-code-incident-overview",
  templateUrl: "./money-code-incident-overview.component.html",
  styleUrls: ["./money-code-incident-overview.component.scss"],
})
export class MoneyCodeIncidentOverviewComponent implements OnInit {
  gDisputeForm: DisputeForm;
  incidentDetailForm: FormGroup;
  canContinue: boolean = false;
  navInfo: NavigationInfo = new NavigationInfo();
  constructor(
    private _progressBarService: ProgressBarService,
    private _navigationService: NavigationService,
    private _dfService: DisputeFormService,
    private _fb: FormBuilder,
    private _translate: TranslateService
  ) {
    this.gDisputeForm = this._dfService.getDisputeForm();
    this.updateProgressBar(stepCondition);
    this.incidentDetailForm = this._fb.group({
      incidentDetail: [this.gDisputeForm.overview, Validators.required],
    });
    this.canContinue = this.gDisputeForm.overview && this.gDisputeForm.overview.trim().length > 0 ? true : false;
  }

  /**
   * Initializes the component and subscribes to changes in the incidentDetail form control.
   * Updates the incident detail in the service, checks form validity, and updates the navigation.
   */
  ngOnInit(): void {
    this.incidentDetailForm.get("incidentDetail")?.valueChanges.subscribe(
      Common.debounce(() => {
        this._dfService.setIncidentDetail(this.incidentDetailForm.value.incidentDetail);
        this.canContinue = this.incidentDetailForm.valid;
        this.updateNav();
      }, 500)
    );
    this.updateNav();
  }
  /**
   * Updates the progress bar with the provided condition and next path.
   *
   * @param condition - The condition to determine the progress bar update.
   * @param nextPath - The next path to navigate to after the progress bar update (optional).
   */
  updateProgressBar(condition: any, nextPath?: NavigationEnum | null) {
    let stepInfo = DFUtils.getStepInfo(this.gDisputeForm.lineOfBusiness as string, currentStep, condition, nextPath);
    this._progressBarService.updateProgressInfo(
      Object.assign(new ProgressInfo(), {
        isVisible: true,
        stepName: "PageName.MCIncidentOverview",
        nextStepName: DFUtils.getNextRouteText(this.gDisputeForm.lineOfBusiness as string, currentStep) as string,
        totalSteps: stepInfo?.maxSteps,
        currentStep: stepInfo?.step,
      })
    );
  }
  /**
   * Updates the navigation information for the money code incident overview component.
   */
  updateNav() {
    this.navInfo.nextRoute = DFUtils.getRoute(this.gDisputeForm.lineOfBusiness as string, NavigationEnum.MC_INCIDENT_OVERVIEW) as string;
    this.navInfo.canContinue = this.canContinue;
    this._navigationService.updateNavigationInfo(this.navInfo);
  }
}
